<template>
  <div class="conversational-registration">
    <!-- Chat Container -->
    <v-card class="chat-container" elevation="2">
      <v-card-title class="chat-header">
        {{ isDealer ? 'Join AutoSyncX™️ Dealer Network - Let\'s Chat!' : 'Join AutoSyncX™️ - Let\'s Chat!' }}
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')" v-if="showCloseButton">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Messages Area -->
      <v-card-text class="messages-container" ref="messagesContainer">
        <div class="messages-list">
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="['message', message.role === 'user' ? 'user-message' : 'assistant-message']"
          >
            <div class="message-content">
              <div class="message-text" v-html="formatMessage(message.content)"></div>
              <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="message assistant-message">
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Input Area -->
      <v-card-actions v-if="conversationState !== 'avatar_upload'" class="chat-input-area">
        <v-text-field
          v-model="currentMessage"
          :placeholder="inputPlaceholder"
          outlined
          dense
          hide-details
          @keyup.enter="sendMessage()"
          :disabled="isTyping || isComplete"
          class="message-input"
        >
          <template v-slot:append>
            <v-btn
              icon
              color="green"
              @click="sendMessage()"
              :disabled="!currentMessage.trim() || isTyping || isComplete"
            >
              <v-icon class="material-icons-round">send</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-card-actions>

      <!-- Avatar Upload Section -->
      <v-card-text v-if="conversationState === 'avatar_upload'" class="text-center">
        <p class="mb-4">{{ lastAssistantMessage }}</p>
        <avatar-cropper
          :upload-handler="handleUploaded"
          :output-mime="cropperOutputMime"
          v-model="changeTrigger"
        />
        <div class="avatar-section">
          <p class="text-h6 text-center mt-3">Your Photo</p>
          <v-avatar v-if="!preview" @click="changeTrigger = true" size="130" color="rgb(190, 190, 190)">
            <v-icon dark class="material-icons-round" style="font-size: 50!important;" size="50">image</v-icon>
          </v-avatar>
          <v-avatar v-else @click="changeTrigger = true" size="130">
            <img :src="preview" alt="Profile Photo">
          </v-avatar>
        </div>
        <div class="mt-4">
          <v-btn color="green" dark @click="confirmAvatarUpload" :disabled="!preview">
            Use This Photo
          </v-btn>
          <v-btn color="grey" text @click="skipAvatar" class="ml-2">
            Skip Photo
          </v-btn>
        </div>
      </v-card-text>

      <!-- Consent Checkboxes -->
      <v-card-text v-if="isComplete && !registrationCompleted" class="consent-section" style="margin:0px;">
        <h4 class="consent-title mb-3">📋 Required Consents</h4>

        <!-- Call Consent -->
        <div class="consent-item mb-3">
          <v-checkbox
            v-model="callConsent"
            color="green"
            class="consent-checkbox"
          >
            <template v-slot:label>
              <span class="consent-label">
                📞 I consent to receive phone calls
                <v-btn
                  icon
                  small
                  @click.stop="toggleCallDisclaimer"
                  class="ml-1"
                >
                  <v-icon small class="material-icons-round">{{ showCallDisclaimer ? 'expand_less' : 'expand_more' }}</v-icon>
                </v-btn>
              </span>
            </template>
          </v-checkbox>

          <v-expand-transition>
            <div v-show="showCallDisclaimer" class="consent-disclaimer mt-2 pa-3">
              <p class="disclaimer-text">
                <strong>📩 Call Consent</strong><br>
                By checking this box, you expressly authorize Round Tables LLC & AutoSyncX™ to call you, either manually or through an automated system. Your agreement to receive calls from Round Tables LLC & AutoSyncX™ is not a condition of this transaction. You are able to opt out of receiving calls at any time by emailing support@AutoSyncX.com.
              </p>
            </div>
          </v-expand-transition>
        </div>

        <!-- SMS Consent -->
        <div class="consent-item mb-4">
          <v-checkbox
            v-model="smsConsent"
            color="green"
            class="consent-checkbox"
          >
            <template v-slot:label>
              <span class="consent-label">
                💬 I consent to receive text messages
                <v-btn
                  icon
                  small
                  @click.stop="toggleSmsDisclaimer"
                  class="ml-1"
                >
                  <v-icon small class="material-icons-round">{{ showSmsDisclaimer ? 'expand_less' : 'expand_more' }}</v-icon>
                </v-btn>
              </span>
            </template>
          </v-checkbox>

          <v-expand-transition>
            <div v-show="showSmsDisclaimer" class="consent-disclaimer mt-2 pa-3">
              <p class="disclaimer-text">
                <strong>📱 SMS/Text Consent</strong><br>
                By checking this box, you authorize Round Tables LLC & AutoSyncX™ to contact you by text. Your agreement to receive SMS messaging from Round Tables LLC & AutoSyncX™ is not a condition of this transaction. You are able to opt-out of such messaging at any time by replying "STOP." Reply "HELP" for assistance. Message frequency may vary. Message and data rates may apply. SMS consent and phone numbers will not be shared with third parties or affiliates for marketing purposes. For further information, please visit our
                <a href="#" @click.prevent="openPrivacyPolicy" class="consent-link">privacy policy</a> or
                <a href="#" @click.prevent="openTermsConditions" class="consent-link">terms and conditions</a> pages.
              </p>
            </div>
          </v-expand-transition>
        </div>
      </v-card-text>

      <!-- Complete Registration Button -->
      <v-card-actions v-if="isComplete &&  !registrationCompleted" class="justify-center">
        <v-btn
          color="green"
          large
          dark
          @click="completeRegistration"
          :loading="completingRegistration"
          v-if="canCompleteRegistration"
          :disabled="!canCompleteRegistration"
        >
          Complete Registration
        </v-btn>

        <div v-if="!canCompleteRegistration" class="consent-warning justify-center mt-2">
          <v-alert
            type="red"
            dense
            text
            class="ma-0"
          >
            Please check both consent boxes to continue
          </v-alert>
        </div>
      </v-card-actions>

      <!-- Success Message with Action Buttons -->
      <v-card-text v-if="registrationCompleted" class="text-center success-section" style="margin:0px;">
        <v-icon color="success" size="48"  class="material-icons-round mb-3">check_circle</v-icon>
        <h3 class="success-title">🎉 Welcome to AutoSyncX™️!</h3>
        <p class="success-message mb-4">Your registration is complete! Here's what you can do next:</p>

        <!-- Action Buttons -->
        <div class="action-buttons mb-4">
          <!-- Refer an Agent Button -->
          <v-btn
            color="green"
            large
            dark
            class="ma-2 action-btn"
            @click="shareReferralLink"
          >
            1️⃣ Refer an Agent
          </v-btn>

          <!-- Send Your Assistant Button -->
          <v-btn
            color="blue"
            large
            dark
            class="ma-2 action-btn"
            @click="shareAssistantLink"
          >
            2️⃣ Send Your Assistant
          </v-btn>
        </div>

        <!-- Login Button (Optional) -->
        <div class="login-section">
          <p class="login-text">Want to see your dashboard or track referrals?</p>
          <v-btn
            color="grey darken-1"
            outlined
            @click="loginToDashboard"
            class="login-btn"
          >
            Login to Dashboard
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import AvatarCropper from 'vue-avatar-cropper'

export default {
  name: 'ConversationalRegistration',
  components: {
    AvatarCropper
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    refId: {
      type: String,
      default: null
    },
    refAgent:{
      type: String,
      default: null
    },
    agentRole: {
      type: String,
      default: null
    },
    selectedProgram: {
      type: String,
      default: null
    },
    showCloseButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sessionId: null,
      agentName: 'David',
      messages: [],
      currentMessage: '',
      isTyping: false,
      isComplete: false,
      registrationCompleted: false,
      completingRegistration: false,
      collectedData: {},
      conversationState: 'greeting',
      // Avatar handling
      changeTrigger: false,
      preview: null,
      photo: null,
      cropperOutputMime: 'image/jpeg',
      // Consent handling
      callConsent: false,
      smsConsent: false,
      showCallDisclaimer: false,
      showSmsDisclaimer: false,
      // Email conflict handling
      emailConflictType: null,
      // Registration success data
      registeredUser: null,
      referralLink: '',
      assistantLink: ''
    }
  },
  computed: {
    inputPlaceholder() {
      if (this.isComplete) return 'Registration ready to complete!';
      if (this.isTyping) return 'Assistant is typing...';
      return 'Type your message...';
    },
    lastAssistantMessage() {
      const assistantMessages = this.messages.filter(m => m.role === 'assistant');
      return assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1].content : '';
    },
    canCompleteRegistration() {
      return this.callConsent && this.smsConsent;
    }
  },
  async mounted() {
    // Try to load existing session first
    if (this.loadSessionFromStorage()) {
      console.log('Loaded existing session from storage, verifying with server...');
      // Verify the session still exists on the server
      await this.verifyServerSession();
    } else {
      console.log('No existing session, initializing new conversation...');
      // Initialize new conversation if no existing session
      await this.initializeConversation();
    }
  },
  methods: {
    async initializeConversation() {
      try {
        const response = await axios.post('/api/conversational-registration/init', {
          domain: this.domain,
          ref_id: this.refId
        });

        if (response.data.flag === 1) {
          this.sessionId = response.data.session_id;
          this.agentName = response.data.agent_name;

          // Add greeting message
          this.messages.push({
            role: 'assistant',
            content: response.data.greeting,
            timestamp: new Date()
          });

          // Add name collection question immediately
          setTimeout(() => {
            this.messages.push({
              role: 'assistant',
              content: "What's your full name?",
              timestamp: new Date()
            });
            this.scrollToBottom();
          }, 1000);

          // Save to localStorage
          this.saveSessionToStorage();
          this.scrollToBottom();
        }
      } catch (error) {
        console.error('Error initializing conversation:', error);
        this.showFallbackGreeting();
      }
    },

    async sendMessage(messageText = null) {
      console.log('sendMessage called with:', messageText, 'Type:', typeof messageText);

      // Handle case where messageText might be an event object
      let userMessage;
      if (messageText && typeof messageText === 'string') {
        userMessage = messageText.trim();
        console.log('Using provided message text:', userMessage);
      } else {
        userMessage = this.currentMessage.trim();
        console.log('Using current message from input:', userMessage);
      }

      if (!userMessage || this.isTyping) {
        console.log('Message empty or typing in progress, returning');
        return;
      }

      // Clear input only if using the input field (not when messageText is provided)
      if (!messageText || typeof messageText !== 'string') {
        this.currentMessage = '';
      }

      // Add user message
      this.messages.push({
        role: 'user',
        content: userMessage,
        timestamp: new Date()
      });

      this.isTyping = true;
      this.scrollToBottom();

      try {
        // Handle email conflict resolution locally
        if (this.conversationState === 'email_conflict_resolution') {
          this.handleEmailConflictResolution(userMessage);
          return;
        }

        const response = await axios.post('/api/conversational-registration/message', {
          session_id: this.sessionId,
          message: userMessage
        });

        if (response.data.flag === 1) {
          // Add assistant response
          this.messages.push({
            role: 'assistant',
            content: response.data.message,
            timestamp: new Date()
          });

          // Update state
          this.conversationState = response.data.conversation_state;
          this.collectedData = response.data.collected_data;
          this.isComplete = response.data.is_complete;

          console.log('Updated conversation state:', this.conversationState);
          console.log('Collected data:', this.collectedData);

          this.saveSessionToStorage();
        }
      } catch (error) {
        console.error('Error sending message:', error);
        this.messages.push({
          role: 'assistant',
          content: "I'm sorry, I'm having trouble right now. Could you try again?",
          timestamp: new Date()
        });
      } finally {
        this.isTyping = false;
        this.scrollToBottom();
      }
    },

    async completeRegistration() {
      this.completingRegistration = true;

      console.log('Starting registration completion...');
      console.log('Collected data:', this.collectedData);

      try {
        // Validate we have all required data
        if (!this.collectedData.first_name || !this.collectedData.last_name || !this.collectedData.email || !this.collectedData.phone) {
          throw new Error('Missing required information. Please complete the conversation first.');
        }

        // Use the same registration endpoint as FormRef.vue (/api/auth/register-card)
        await this.submitToRegisterCard();

      } catch (error) {
        console.error('Error completing registration:', error);
        this.$toast.error(`Failed to complete registration: ${error.message}`, {
          position: 'top-right'
        });
        this.completingRegistration = false;
      }
    },

    async submitToRegisterCard() {
      try {
        // Create FormData for submission (same as FormRef checkForm)
        const formData = new FormData();
        formData.append('first_name', this.collectedData.first_name);
        formData.append('last_name', this.collectedData.last_name);
        formData.append('email', this.collectedData.email);
        formData.append('phone', this.collectedData.phone);
        formData.append('user_name', this.collectedData.email);

        // Add referral info if available
        if (this.refId) {
          formData.append('company_id', this.refId);
        }
        if(this.refAgent)
          formData.append('flag', this.refAgent);

        // Add agent role - prioritize collected data from conversation, then URL parameter
        const agentRole = this.collectedData.agent_role || this.agentRole;
        if (agentRole) {
          formData.append('agent_role', agentRole);
        }

        // Add selected program if provided from URL
        if (this.selectedProgram) {
          formData.append('selected_program', this.selectedProgram);
        }

        // Add conversation metadata
        if (this.collectedData.tone_preference) {
          formData.append('conversation_tone', this.collectedData.tone_preference);
        }

        // Add consent data
        formData.append('call_consent', this.callConsent ? '1' : '0');
        formData.append('sms_consent', this.smsConsent ? '1' : '0');

        // Add avatar if uploaded
        if (this.photo) {
          formData.append('file_photo', this.photo);
        }

        console.log('Submitting to register-card with data:', {
          first_name: this.collectedData.first_name,
          email: this.collectedData.email,
          phone: this.collectedData.phone,
          ref_id: this.refId,
          has_photo: !!this.photo
        });

        // Submit to register-card API
        const response = await axios.post('/api/auth/register-card', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Handle different API response flags
        if (response.data.flag === 1) {
          // Success - Registration completed
          this.handleRegistrationSuccess(response.data);
        } else if (response.data.flag === 2) {
          // Multiple company users exist with same email
          this.handleMultipleCompanyUsers();
        } else if (response.data.flag === 3) {
          // Client user already exists with this email
          this.handleExistingClientUser();
        } else {
          // Other error
          throw new Error(response.data.message || 'Registration failed');
        }
      } catch (error) {
        console.error('Error submitting to register-card:', error);

        // Handle specific HTTP status codes
        if (error.response && error.response.status === 400) {
          const responseData = error.response.data;

          if (responseData.flag === 2) {
            this.handleMultipleCompanyUsers();
          } else if (responseData.flag === 3) {
            this.handleExistingClientUser();
          } else {
            this.$toast.error(`Registration failed: ${responseData.message || 'Please check your information and try again.'}`, {
              position: 'top-right',
              duration: 6000
            });
          }
        } else {
          // Network or other errors
          this.$toast.error('Registration failed. Please check your connection and try again.', {
            position: 'top-right',
            duration: 6000
          });
        }
      } finally {
        this.completingRegistration = false;
      }
    },



    handleUploaded(cropper) {
      let self = this;
      let image = cropper.getCroppedCanvas().toDataURL(this.cropperOutputMime);
      let img = this.dataURLtoFile(image, 'avatar.jpg');
      self.preview = image;
      self.photo = img;
      console.log('Avatar uploaded and processed');
    },

    dataURLtoFile(dataurl, filename) {
      var arr = dataurl.split(','),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);

      while(n--){
          u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename, {type:mime});
    },

    async confirmAvatarUpload() {
      if (this.photo) {
        console.log('Avatar confirmed, continuing conversation...');
        // Send message to continue conversation
        await this.sendMessage('I uploaded my photo');
      } else {
        this.$toast.error('Please select a photo first', {
          position: 'top-right'
        });
      }
    },

    async skipAvatar() {
      console.log('Avatar skipped, continuing conversation...');
      // Send message to skip avatar
      await this.sendMessage('skip');
    },

    async autoLoginUser(result) {
      try {
        localStorage.setItem("token", response.data.token.token);
        localStorage.setItem("user_id", result.user_id);
        localStorage.setItem("email", result.email);
        localStorage.setItem("phone", result.phone_m);
        localStorage.setItem("name", result.first_name+' '+result.last_name);
        localStorage.setItem("is_admin",0);
        localStorage.setItem("role", JSON.stringify(result.roles[0]));
        const domain = response.data.domain
        let url = 'http://'+domain+'.'+this.getParentDomain();
        window.open(url);
        //self.$router.push('/login');
        // if(is_verified == 0)
        //     Vue.$toast.success('You have successfuly registered. Please check your email for login', {
        //     position: 'top-right'
        //     });
        // else
        //     Vue.$toast.success('You have successfuly registered. Please check your email for login', {
        //     position: 'top-right'
        //     });

        this.$router.push("/");

        console.log('User auto-logged in successfully');
      } catch (error) {
        console.error('Error during auto-login:', error);
      }
    },

    showFallbackGreeting() {
      this.messages.push({
        role: 'assistant',
        content: `Hey! ${this.agentName} told me you might be a perfect fit for what we're building with AutoSyncX™️ — excited to get you plugged in. Mind if I get to know you a little first? 😊`,
        timestamp: new Date()
      });
      this.scrollToBottom();
    },

    formatMessage(content) {
      // Convert line breaks to <br> tags and handle basic formatting
      return content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    saveSessionToStorage() {
      if (this.sessionId) {
        const sessionData = {
          sessionId: this.sessionId,
          agentName: this.agentName,
          messages: this.messages,
          collectedData: this.collectedData,
          conversationState: this.conversationState,
          isComplete: this.isComplete,
          refId: this.refId,
          domain: this.domain,
          // Save avatar data
          preview: this.preview,
          photo: this.photo ? {
            name: this.photo.name,
            size: this.photo.size,
            type: this.photo.type,
            lastModified: this.photo.lastModified
          } : null,
          // Save consent states
          callConsent: this.callConsent,
          smsConsent: this.smsConsent,
          // Save email conflict state
          emailConflictType: this.emailConflictType,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('conversational_registration_session', JSON.stringify(sessionData));
        console.log('Session saved to localStorage');
      }
    },

    loadSessionFromStorage() {
      const savedSession = localStorage.getItem('conversational_registration_session');
      if (savedSession) {
        try {
          const sessionData = JSON.parse(savedSession);

          // Check if session is not too old (24 hours)
          const sessionAge = sessionData.timestamp ?
            new Date() - new Date(sessionData.timestamp) :
            (sessionData.messages.length > 0 ?
              new Date() - new Date(sessionData.messages[sessionData.messages.length - 1].timestamp) :
              Infinity);

          if (sessionAge < 24 * 60 * 60 * 1000) { // 24 hours
            this.sessionId = sessionData.sessionId;
            this.agentName = sessionData.agentName;
            this.messages = sessionData.messages || [];
            this.collectedData = sessionData.collectedData || {};
            this.conversationState = sessionData.conversationState || 'greeting';
            this.isComplete = sessionData.isComplete || false;
            this.refId = sessionData.refId;
            this.domain = sessionData.domain;

            // Restore avatar data
            if (sessionData.preview) {
              this.preview = sessionData.preview;
            }

            // Restore consent states
            if (sessionData.callConsent !== undefined) {
              this.callConsent = sessionData.callConsent;
            }
            if (sessionData.smsConsent !== undefined) {
              this.smsConsent = sessionData.smsConsent;
            }

            // Restore email conflict state
            if (sessionData.emailConflictType !== undefined) {
              this.emailConflictType = sessionData.emailConflictType;
            }

            console.log('Session restored from localStorage:', {
              sessionId: this.sessionId,
              messageCount: this.messages.length,
              conversationState: this.conversationState,
              collectedData: this.collectedData
            });

            this.scrollToBottom();
            return true; // Session loaded successfully
          } else {
            console.log('Session expired, starting fresh');
            this.clearSessionStorage();
            return false;
          }
        } catch (error) {
          console.error('Error loading session from storage:', error);
          this.clearSessionStorage();
          return false;
        }
      }
      return false; // No session found
    },

    async verifyServerSession() {
      try {
        console.log('Verifying server session:', this.sessionId);

        // Try to send a verification message to check if session exists
        const response = await axios.post('/api/conversational-registration/verify', {
          session_id: this.sessionId
        });

        if (response.data.flag === 1) {
          console.log('Server session verified, continuing conversation');
          // Session exists on server, we can continue
          return true;
        } else {
          console.log('Server session not found, reinitializing...');
          // Session doesn't exist on server, need to reinitialize
          await this.reinitializeWithExistingData();
          return false;
        }
      } catch (error) {
        console.error('Error verifying server session:', error);
        // If verification fails, try to reinitialize with existing data
        await this.reinitializeWithExistingData();
        return false;
      }
    },

    async reinitializeWithExistingData() {
      console.log('Reinitializing conversation with existing data...');

      // Store current data
      const existingMessages = [...this.messages];
      const existingData = {...this.collectedData};
      const existingState = this.conversationState;
      const existingComplete = this.isComplete;

      try {
        // Initialize new conversation
        const response = await axios.post('/api/conversational-registration/init', {
          domain: this.domain,
          ref_id: this.refId,
          agent_name: this.agentName
        });

        if (response.data.flag === 1) {
          // Update with new session ID but keep existing data
          this.sessionId = response.data.session_id;

          // Restore the conversation state on the server by sending collected data
          if (Object.keys(existingData).length > 0) {
            await this.restoreServerState(existingData, existingState);
          }

          console.log('Conversation reinitialized with session:', this.sessionId);
          this.saveSessionToStorage();
        }
      } catch (error) {
        console.error('Error reinitializing conversation:', error);
        // If all else fails, start fresh but keep the UI state
        this.showFallbackGreeting();
      }
    },

    async restoreServerState(collectedData, conversationState) {
      try {
        console.log('Restoring server state:', { collectedData, conversationState });

        // Send a special restore message to sync the server state
        const response = await axios.post('/api/conversational-registration/restore', {
          session_id: this.sessionId,
          collected_data: collectedData,
          conversation_state: conversationState
        });

        if (response.data.flag === 1) {
          console.log('Server state restored successfully');
        }
      } catch (error) {
        console.error('Error restoring server state:', error);
      }
    },

    toggleCallDisclaimer() {
      this.showCallDisclaimer = !this.showCallDisclaimer;
    },

    toggleSmsDisclaimer() {
      this.showSmsDisclaimer = !this.showSmsDisclaimer;
    },

    openPrivacyPolicy() {
      window.open('https://autosyncx.com/privacy-policy', '_blank');
    },

    openTermsConditions() {
      window.open('https://autosyncx.com/terms-conditions', '_blank');
    },

    handleRegistrationSuccess(responseData) {
      this.registrationCompleted = true;
      this.clearSessionStorage();

      // Store user data for buttons
      this.registeredUser = responseData.user;

      // Show success message
      this.$toast.success('🎉 Registration successful! Welcome to AutoSyncX™️!', {
        position: 'top-right',
        duration: 5000
      });

      // Generate sharing links
      this.generateSharingLinks(responseData);
    },
    getParentDomain() {
           const hostname = window.location.hostname;
           const domainParts = hostname.split('.');
 
           // Assuming the domain is of the form subdomain.example.com
           if (domainParts.length >= 2) {
               const parentDomain = domainParts.slice(-2).join('.');
               return parentDomain;
           } else {
               return hostname; // For cases like localhost or single part domains
           }
       },

    generateSharingLinks(responseData) {
      if (this.registeredUser && this.registeredUser.user_id) {
        // Generate referral link for recruiting agents
        this.referralLink = 'http://'+responseData.domain+'.'+this.getParentDomain();

        // Generate assistant link using domain or user_id
        const domain = responseData.domain || this.domain || 'default';
        this.assistantLink = this.referralLink+`/assistant`;

        console.log('Generated links:', {
          referralLink: this.referralLink,
          assistantLink: this.assistantLink
        });
      }
    },

    shareReferralLink() {
      const message = `🚀 Join my team at AutoSyncX™️! Sign up using my referral link and start earning commissions: ${this.referralLink}`;

      if (navigator.share) {
        // Use native sharing if available
        navigator.share({
          title: 'Join AutoSyncX™️ - Refer an Agent',
          text: message,
          url: this.referralLink
        }).catch(console.error);
      } else {
        // Fallback to copying to clipboard
        this.copyToClipboard(this.referralLink, 'Referral link copied to clipboard! Share it to recruit new agents.');

        // Also show the message for easy sharing
        this.$toast.info(`Share this message: ${message}`, {
          position: 'top-right',
          duration: 10000
        });
      }
    },

    shareAssistantLink() {
      const message = `🤖 Meet my AI Digital Assistant! Get personalized vehicle recommendations and instant support: ${this.assistantLink}`;

      if (navigator.share) {
        // Use native sharing if available
        navigator.share({
          title: 'AutoSyncX™️ Digital Assistant',
          text: message,
          url: this.assistantLink
        }).catch(console.error);
      } else {
        // Fallback to copying to clipboard
        this.copyToClipboard(this.assistantLink, 'Assistant link copied to clipboard! Share it with potential buyers.');

        // Also show the message for easy sharing
        this.$toast.info(`Share this message: ${message}`, {
          position: 'top-right',
          duration: 10000
        });
      }
    },

    copyToClipboard(text, successMessage) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$toast.success(successMessage, {
            position: 'top-right',
            duration: 4000
          });
        }).catch(() => {
          this.fallbackCopyToClipboard(text, successMessage);
        });
      } else {
        this.fallbackCopyToClipboard(text, successMessage);
      }
    },

    fallbackCopyToClipboard(text, successMessage) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        this.$toast.success(successMessage, {
          position: 'top-right',
          duration: 4000
        });
      } catch (err) {
        this.$toast.error('Could not copy link. Please copy manually: ' + text, {
          position: 'top-right',
          duration: 8000
        });
      }

      document.body.removeChild(textArea);
    },

    loginToDashboard() {
      // Auto-login the user and redirect to dashboard
      if (this.registeredUser && this.registeredUser.user_id) {
        this.autoLoginUser(this.registeredUser);

        this.$toast.success('Logging you in...', {
          position: 'top-right',
          duration: 3000
        });

        // Redirect to dashboard after short delay
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      } else {
        // Fallback to regular login page
        window.location.href = '/login';
      }
    },

    handleMultipleCompanyUsers() {
      // Flag 2: Multiple company users exist with same email
      this.$toast.error('⚠️ Multiple business accounts found with this email address.', {
        position: 'top-right',
        duration: 8000
      });

      // Add conversational message with options
      this.addAssistantMessage(
        `🏢 I found multiple business accounts associated with ${this.collectedData.email}. This email is already registered as a business user.\n\n` +
        `Here are your options:\n` +
        `1️⃣ Type "support" to contact our support team\n` +
        `2️⃣ Type "change email" to use a different email address\n` +
        `3️⃣ Or just type your new email address directly\n\n` +
        `What would you like to do?`
      );

      // Set state to handle the user's choice
      this.conversationState = 'email_conflict_resolution';
      this.emailConflictType = 'multiple_company';
      this.isComplete = false;
      this.saveSessionToStorage();
    },

    handleExistingClientUser() {
      // Flag 3: Client user already exists with this email
      this.$toast.warning('📧 This email is already registered as a client account.', {
        position: 'top-right',
        duration: 8000
      });

      // Add conversational message with options
      this.addAssistantMessage(
        `👤 I found that ${this.collectedData.email} already exists as a client account.\n\n` +
        `Here are your options:\n` +
        `1️⃣ Type "login" to go to the login page\n` +
        `2️⃣ Type "change email" to use a different email address\n` +
        `3️⃣ Or just type your new email address directly\n\n` +
        `What would you like to do?`
      );

      // Set state to handle the user's choice
      this.conversationState = 'email_conflict_resolution';
      this.emailConflictType = 'existing_client';
      this.isComplete = false;
      this.saveSessionToStorage();
    },

    goBackToEmailCollection() {
      // Reset email and go back to email collection state
      this.collectedData.email = '';
      this.conversationState = 'email_collection';
      this.isComplete = false;
      this.emailConflictType = null; // Reset conflict type

      // Add message to conversation only if not already added
      if (this.conversationState !== 'email_collection') {
        this.messages.push({
          role: 'assistant',
          content: "No problem! Let's try with a different email address. What's your email?",
          timestamp: new Date()
        });
      }

      this.saveSessionToStorage();
      this.scrollToBottom();
    },

    contactSupport(reason) {
      // Open support contact with context
      const subject = reason === 'multiple_business_accounts' ?
        'Multiple Business Accounts Issue' :
        'Registration Support';

      const body = `Hello,\n\nI need help with my registration:\n\nEmail: ${this.collectedData.email}\nName: ${this.collectedData.first_name} ${this.collectedData.last_name}\nIssue: ${reason}\n\nPlease help me resolve this.\n\nThank you!`;

      const mailtoLink = `mailto:support@autosyncx.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);

      // Also show contact info
      this.$toast.info('📞 You can also call us at: (555) 123-4567', {
        position: 'top-right',
        duration: 10000
      });
    },

    addAssistantMessage(content) {
      this.messages.push({
        role: 'assistant',
        content: content,
        timestamp: new Date()
      });
      this.scrollToBottom();
    },

    handleEmailConflictResolution(userMessage) {
      const message = userMessage.toLowerCase().trim();

      if (message === 'support') {
        // User wants to contact support
        this.addAssistantMessage("I'll help you contact our support team. Opening your email client now...");
        this.contactSupport(this.emailConflictType === 'multiple_company' ? 'multiple_business_accounts' : 'existing_client_account');

      } else if (message === 'login') {
        // User wants to login (only for existing client)
        if (this.emailConflictType === 'existing_client') {
          this.addAssistantMessage("Perfect! I'll redirect you to the login page in a moment...");
          setTimeout(() => {
            this.redirectToLogin();
          }, 2000);
        } else {
          this.addAssistantMessage("Login option is not available for this situation. Please choose 'support' or provide a new email address.");
        }

      } else if (message === 'change email' || message === 'different email' || message === 'new email') {
        // User wants to change email
        this.addAssistantMessage("No problem! Let's use a different email address. What's your new email?");
        this.goBackToEmailCollection();

      } else if (this.isValidEmail(message)) {
        // User provided a new email directly
        this.addAssistantMessage(`Great! I'll update your email to ${message}. Let me check if this one is available...`);

        // Update the collected data with new email
        this.collectedData.email = message;

        // Try registration again with new email
        setTimeout(() => {
          this.completeRegistration();
        }, 1500);

      } else {
        // Invalid input
        const conflictTypeText = this.emailConflictType === 'multiple_company' ?
          'multiple business accounts' : 'existing client account';

        this.addAssistantMessage(
          `I didn't understand that. Since we found ${conflictTypeText} with your email, please:\n\n` +
          `• Type "support" to contact our team\n` +
          `• Type "change email" to use a different email\n` +
          `• Or just type your new email address directly\n\n` +
          `What would you like to do?`
        );
      }

      this.isTyping = false;
      this.saveSessionToStorage();
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    redirectToLogin() {
      // Clear current session
      this.clearSessionStorage();

      // Show message
      this.$toast.info('Redirecting to login page...', {
        position: 'top-right',
        duration: 3000
      });

      // Redirect to login page after short delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    },

    clearSessionStorage() {
      localStorage.removeItem('conversational_registration_session');
    }
  }
}
</script>

<style scoped>
.conversational-registration {
  max-width: 500px;
  margin: 0 auto;
}

.chat-container {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-header {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  padding: 16px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f5f5f5;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message {
  display: flex;
  max-width: 80%;
}

.user-message {
  align-self: flex-end;
}

.user-message .message-content {
  background: #4CAF50;
  color: white;
  border-radius: 18px 18px 4px 18px;
}

.assistant-message {
  align-self: flex-start;
}

.assistant-message .message-content {
  background: white;
  color: #333;
  border-radius: 18px 18px 18px 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-content {
  padding: 12px 16px;
  position: relative;
}

.message-text {
  line-height: 1.4;
  word-wrap: break-word;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.chat-input-area {
  padding: 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.message-input {
  flex: 1;
}

.success-title {
  color: #4CAF50;
  margin-bottom: 8px;
}

.success-message {
  color: #666;
  font-size: 14px;
}

.avatar-section {
  text-align: center;
  margin: 20px 0;
}

.avatar-section .v-avatar {
  cursor: pointer;
  border: 2px dashed #ccc;
  transition: border-color 0.3s;
}

.avatar-section .v-avatar:hover {
  border-color: #4CAF50;
}

/* Consent section styles */
.consent-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 16px;
}

.consent-title {
  color: #333;
  font-weight: 600;
  text-align: center;
}

.consent-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: white;
  padding: 8px;
}

.consent-checkbox {
  margin: 0;
}

.consent-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

.consent-disclaimer {
  background-color: #f5f5f5;
  border-left: 4px solid #4CAF50;
  border-radius: 4px;
  margin-left: 32px;
}

.disclaimer-text {
  font-size: 12px;
  line-height: 1.4;
  color: #555;
  margin: 0;
}

.consent-link {
  color: #4CAF50;
  text-decoration: underline;
}

.consent-link:hover {
  color: #45a049;
}

.consent-warning {
  width: 100%;
  text-align: center;
}

/* Success section styles */
.success-section {
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  margin: 16px;
}

.success-title {
  color: #28a745;
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 8px;
}

.success-message {
  color: #6c757d;
  font-size: 16px;
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.action-btn {
  min-width: 280px;
  height: 56px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.2);
}

.login-section {
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
  margin-top: 20px;
}

.login-text {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 12px;
}

.login-btn {
  min-width: 200px;
  height: 44px;
  font-weight: 500;
  border-radius: 6px;
  text-transform: none;
}

/* Mobile responsiveness */
@media (min-width: 600px) {
  .action-buttons {
    flex-direction: row;
    justify-content: center;
  }

  .action-btn {
    min-width: 240px;
  }
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .conversational-registration {
    max-width: 100%;
  }

  .chat-container {
    height: 500px;
  }

  .message {
    max-width: 90%;
  }

  .avatar-section {
    margin: 15px 0;
  }
}
</style>
